import type { Domain, Status } from '@asvise/household-engine/vocabulary'

/**
 * The words the interface puts around a finding.
 *
 * One sentence per reason code, which is what lets the interface explain a
 * status without recomputing it: the engine emits a token and this file says
 * what that token means to a household.
 *
 * It held two languages until the second was removed, which was a decision
 * taken rather than a thing overlooked. The shape that made a translation
 * possible is still here, because it is the same shape that keeps the wording
 * out of the rules: a code is not a sentence, so the sentence can change
 * without the finding being recomputed.
 */

/**
 * Which wording explained a result, stamped on every stored one.
 *
 * Framework section 10.3's generated explanation version. It is not derivable
 * from the engine's output: the engine emits reason codes and this file turns
 * each into a sentence in one of two languages, so what a household actually
 * read is this file's business. Bumped whenever a phrase below changes, in the
 * same way the consent copy version is, and for the same reason: a change to
 * the words a person was shown must be visible in the record rather than
 * silently retroactive.
 */
export const EXPLANATION_VERSION = '2026.10.01'

/**
 * What we found, per reason code.
 *
 * Deliberately not the engine's own trigger sentence, which carries figures
 * interpolated into it. These say the same thing without them, and the card
 * prints the figures beside them, so a figure can be checked against its own
 * evidence rather than read inside a sentence.
 */
export const FINDING: Record<string, string> = {
  MATERIAL_SHORTFALL: 'You have some cover for this, and less than the amount worked out from your answers.',
  NO_VERIFIED_COVER: 'We did not find any cover for this in the documents you gave us.',
  NO_HOSPITAL_COVER_CONFIRMED: 'No hospital cover naming this person was found.',
  COVER_MEETS_CALCULATED_NEED: 'The cover we found is at least the amount worked out from your answers. That is not a statement that a claim would be paid.',
  COST_SHARING_FALLS_TO_HOUSEHOLD: 'There is hospital cover, and a share of any bill still falls to your family.',
  COST_SHARING_NOT_READ: 'We found hospital cover and could not read what you would pay towards a bill.',
  NEED_INPUTS_MISSING: 'Some of the answers this calculation needs have not been given.',
  DISABILITY_SCENARIO_NOT_COLLECTED: 'We have reported the disability benefit and not worked out whether it would be enough, because a disability changes what a family spends as well as what it earns and we have not asked about that.',
  UNPAID_CONTRIBUTION_NOT_SIZED: 'This person brings no income in, and your family would still have to pay for what they do. The amount above is about replacing income, so it does not describe this.',
  INSURED_PERSON_NOT_CONFIRMED: 'A benefit was found and nobody has confirmed which member of your family it insures.',
  COVER_NOT_SUMMABLE: 'More than one policy answers this. Whether they pay together or one after the other is a question about the wordings.',
  BUFFER_BELOW_GUIDANCE: 'Your savings would cover less than the six months public guidance suggests building towards.',
  BUFFER_AT_OR_ABOVE_GUIDANCE: 'Your savings would cover six months of essentials or more.',
  BUFFER_NOT_ESTABLISHED: 'We do not know your savings or your monthly essentials yet.',
  EXPOSURE_DOES_NOT_APPLY: 'This does not apply to your family from the answers given.',
  EXPOSURE_NOT_ESTABLISHED: 'We do not yet know enough about who lives here to say.',
}

export const UI: Record<string, string> = {
  whatWeFound: 'What we found',
  whyItMatters: 'Why it matters to your family',
  whatEvidence: 'What evidence was used',
  whatUncertain: 'What remains uncertain',
  whatNext: 'What you can do next',
  yourCalculation: 'Worked out from your answers',
  ruleOfThumb: 'A general rule of thumb',
  scenarioTotal: 'What the scenario you chose would need',
  /*
    Not "waiting on your answers", which was the first wording and was wrong on a
    family that had answered everything: the group holds areas the engine cannot
    size, and some of them are not waiting on anything a household can say. The
    caregiver's unpaid work and disability need figures the questionnaire does not
    ask for. What is true of all of them is what the rows already say.
  */
  notRanked: 'More information needed',
  notLookedAt: 'Not looked at in this screening',
  aboutScreening: 'That is a statement about what we screened, not about your family.',
  nothingUncertain: 'Nothing we could identify from what you gave us. That is not the same as nothing at all: exclusions and definitions live in the policy wording, which this reading did not cover.',
  screenedOn: 'Screened',
  againstRuleSet: 'against rule set',
  forPerson: 'for',
  decidedBy: 'Decided by',
}

export const DOMAIN: Partial<Record<Domain, string>> = {
  EMERGENCY_LIQUIDITY: 'Emergency savings',
  HOSPITAL_SURGICAL: 'Hospital and surgery',
  DEATH_INCOME_CONTINUITY: 'Family income if someone dies',
  DISABILITY_TPD: 'Permanent disability',
  CRITICAL_ILLNESS_RECOVERY: 'Income during a serious illness',
  LONG_TERM_CARE: 'Long-term care',
}

export const STATUS: Partial<Record<Status, string>> = {
  HUMAN_REVIEW_REQUIRED: 'Needs a person to look at it',
  DATA_INCOMPLETE: 'More information needed',
  COVERAGE_NOT_VERIFIED: 'Cover not verified',
  ATTENTION_REQUIRED: 'Requires attention',
  PARTIALLY_ADDRESSED: 'Partly addressed',
  NO_OBVIOUS_GAP: 'No obvious gap',
  NOT_APPLICABLE: 'Does not apply',
}

/**
 * `BAND_NOTE` is the engine's, and the card prints it from there.
 *
 * A copy of it lived here as `BAND_NOTE_SAID`, so the note could be translated
 * with the engine's English as a fallback. With one language that is two copies
 * of one sentence, and of every sentence in this product it is the worst one to
 * let drift: it is the line that says a band is a reading order and not advice.
 */
